import { MaintenanceWindowGridView } from '../view/MaintenanceWindowGridView';
import { templateMaintenanceWindowGrid } from '../view/template';
import { BaseWebComponentController, WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { MaintenanceWindowGridWebComponent } from '..';
import { MaintenanceWindowsEvents } from '../types';
import { MaintenanceWindowGridRepository } from '../repository/MaintenanceWindowGridRepository';
import { GridEvents } from '../../../grid/types/common';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { ToastPositionX, ToastPositionY, ToastSeverity } from '../../../ui/gzToastNotification/type';
import { MaintenanceWindowsDeleteDialogWebComponent } from '../../../maintenanceWindows/maintenanceWindowsDeleteDialog';
import {Rights} from "@nirvana/shared/constants";

export class MaintenanceWindowGridController extends BaseWebComponentController {
  public component: MaintenanceWindowGridWebComponent;
  public view: MaintenanceWindowGridView;
  public viewConstructor: typeof MaintenanceWindowGridView;
  public dataRepository: MaintenanceWindowGridRepository;
  public dataRepositoryConstructor: typeof MaintenanceWindowGridRepository;

  private selectedItemsData: Array<any> = [];

  constructor(component: MaintenanceWindowGridWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: MaintenanceWindowGridView,
      viewTemplate: templateMaintenanceWindowGrid,
      dataRepositoryConstructor: MaintenanceWindowGridRepository,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$addWindowButton,
        event: 'click',
        callback: this.onAddWindowButtonClick,
      },
      {
        element: this.view.dom.$deleteWindowButton,
        event: 'click',
        callback: this.triggerDelete,
      },
      {
        element: this.view.dom.$gridView,
        event: GridEvents.SELECT_ROWS,
        callback: this.selectedItems,
      },
      {
        element: this.view.dom.$gridView,
        event: 'editItem',
        callback: this.editItem,
      },
    ];
  }

  editItem(ev) {
    this.component.dispatchEvent(
      new CustomEvent(MaintenanceWindowsEvents.ADD_MAINTENANCE_WINDOW, {
        detail: ev.detail,
        bubbles: true,
      })
    );
  }

  onAddWindowButtonClick(ev) {
    this.component.dispatchEvent(
      new CustomEvent(MaintenanceWindowsEvents.ADD_MAINTENANCE_WINDOW, {
        detail: {},
        bubbles: true,
      })
    );
  }

  async triggerDelete() {
    if (this.selectedItemsData.length === 0) {
      return;
    }

    // selected items ids for backend sending
    const selectedItemsIds = this.selectedItemsData.map(item => {
      return { _id: item._id };
    });

    const selectedItemsIdsArray = this.selectedItemsData.map(item => {
      return item._id;
    });

    // show confirmation dialog when delete button hit
    this.showConfirmationDialog(selectedItemsIdsArray, async () => {
      // params for backend call
      const params = selectedItemsIds;

      const unknownErrorMessage = this.dependencies.translateFn('Maintenance window not deleted');
      let removeError = null;

      try {
        // call remove records
        const removeResponse = await this.dataRepository.removeRecords(params);

        if (!removeResponse.success) {
          // unknown error
          removeError = unknownErrorMessage;
        }
      } catch (error) {
        removeError = error.message || unknownErrorMessage;
      }

      if (removeError) {
        // show error toast
        this.showErrorToast(removeError);
        return;
      }

      // show success toast
      this.showSuccessToast();

      // reload grid data
      this.view.dom.$gridView.propertiesSet = true;
    });
  }

  private showSuccessToast() {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: this.dependencies.translateFn('Maintenance window deleted successfully'),
      severity: ToastSeverity.SUCCESS,
      y: ToastPositionY.TOP,
    });
  }

  private showErrorToast(errorMessage: string) {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: errorMessage,
      severity: ToastSeverity.ERROR,
      y: ToastPositionY.TOP,
    });
  }

  private showWarningToast(warning: string) {
    const errorMessage = this.dependencies.translateFn(warning);

    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: errorMessage,
      severity: ToastSeverity.WARNING,
      y: ToastPositionY.TOP,
    });
  }

  selectedItems(items: WebComponentEvent) {
    // global selected items
    this.selectedItemsData = items.detail.selectedItemsData;

    // if any selection, enable delete button
    if (this.selectedItemsData.length === 1 && Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
      this.view.dom.$deleteWindowButton.disabled = false;
    } else {
      this.view.dom.$deleteWindowButton.disabled = true;
    }
  }

  showConfirmationDialog(maintenanceWindowIds, confirmationCallback) {
    const windowBodyHtml = `<p>${this.dependencies.translateFn(
      'If you delete this maintenance window, the Patch Management module will be automatically disabled until you assign a new maintenance window to the impacted policies.'
    )}</p>`;

    const windowFooterHtml = `<gz-button id='confirm' type='outline' color='negative'>
       ${this.dependencies.translateFn('DELETE')}
      </gz-button>
      <gz-button id='cancel'>
        ${this.dependencies.translateFn('CANCEL')}
      </gz-button>`;
    const windowTitle = this.dependencies.translateFn('Delete maintenance window?');

    const cancelCallback = () => {
      // do nothing
    };

    const dialogBody = new MaintenanceWindowsDeleteDialogWebComponent();
    dialogBody.injectDependencies(this.dependencies);
    dialogBody.companyId = this.dependencies.getActiveCompany().companyId;
    dialogBody.maintenanceWindowIds = maintenanceWindowIds;
    dialogBody.start().then().catch();

    GzDialogWebComponent.computeCustomDialog(
      dialogBody,
      windowFooterHtml,
      windowTitle,
      cancelCallback,
      confirmationCallback
    );
  }
}
